import React from 'react';
import { motion } from 'framer-motion';
import { BadgeCheck, User } from 'lucide-react';
import { BrandMark, type PublicBrand } from '../shopper/Brand';

interface Creator {
  userId: string;
  displayName: string;
  verified: boolean;
  /** The brand's logos and colours (#462). */
  brand?: PublicBrand;
}

interface CreatorInfoProps {
  creator: Creator | null;
}

const CreatorInfo: React.FC<CreatorInfoProps> = ({ creator }) => {
  if (!creator) {
    return (
      <div className="flex items-center gap-2 text-[var(--v-text-3)]">
        <User size={18} />
        <span className="text-sm">Unknown Creator</span>
      </div>
    );
  }

  return (
    <motion.div
      className="flex items-center gap-2"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      {/* The brand's square logo (#462); its initial when it has none */}
      <BrandMark
        brand={creator.brand}
        size={38}
        fallback={
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-william-300 to-william-500 flex items-center justify-center text-night-950 font-bold text-sm">
            {creator.displayName.charAt(0).toUpperCase()}
          </div>
        }
      />

      {/* Creator Name and Badge */}
      <div className="flex flex-col">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-[var(--v-text)]">{creator.displayName}</span>
          {creator.verified && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 15, delay: 0.3 }}
              className="relative group"
            >
              <BadgeCheck
                size={18}
                className="text-[var(--v-trust)]"
                aria-label="Verified brand"
              />
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                Verified brand
              </div>
            </motion.div>
          )}
        </div>
        {/* Brand names are self-declared; only an account BasiQR checked is a verified brand (pentest C1). */}
        <span className="text-xs text-[var(--v-text-3)]">{creator.verified ? 'Verified brand' : 'Brand not verified'}</span>
      </div>
    </motion.div>
  );
};

export default CreatorInfo;

